import { createContext, useCallback, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Modal } from 'antd'
import { differenceBy } from 'lodash'

import useSectionGroups from '../../../utils/api/hooks/useSectionGroups'
import { Actions } from '../../../utils/events'
import { getIdOrNull, isObject, isEmptyArray, isOptimistic, isNonEmptyArray } from '../../../utils'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import SingleSectionGroupForm from './SingleSectionGroupForm'
import { UpdateModes } from '../../../utils/enums/enums'
import { ApiErrors, getAllSectionGroups } from '../../../utils/api/sectionGroup'
import TransferSectionsPanel from './TransferSectionsPanel'
import useRequest from '../../../hooks/useRequest'
import { Properties } from '../../../utils/entities'

export const SectionGroupModalContext = createContext()

const DEFAULT_FORM_HANDLER = { data: null, callback: null, error: null }

const SectionGroupModalProvider = ({ children, mode = UpdateModes.OPTIMISTIC }) => {
  const t = useSelector(selectTranslate)

  const [loading, setLoading] = useState(false)
  const [formHandler, setFormHandler] = useState(DEFAULT_FORM_HANDLER)
  const [toTransferSections, setToTransferSections] = useState([])

  const filters = useMemo(() => {
    const filters = {}

    if (!isOptimistic(formHandler.data)) {
      filters.institutional = getIdOrNull(formHandler.data?.institution) !== null
    }

    if (isNonEmptyArray(toTransferSections)) {
      filters.sections = toTransferSections.map(getIdOrNull)
    }

    return filters
  }, [formHandler.data?.institution, toTransferSections])

  const sectionsGroups = useRequest({
    request: getAllSectionGroups,
    parameters: useMemo(() => ({
      pageSize: 200,
      filters
    }), [filters])
  })

  const sectionGroupHandler = useSectionGroups({ mode, callback: formHandler.callback, throwErrors: true })

  const handleSubmit = useCallback(sectionGroup => {
    setLoading(true)
    let isError = false
    let sectionsDifference = []

    sectionGroupHandler.upsert(sectionGroup, formHandler.data)
      .catch(error => {
        isError = true

        const key = error.message
        if ([ApiErrors.INSTITUTION_COMBINATION_EXISTS, ApiErrors.SECTORS_COMBINATION_EXISTS].includes(ApiErrors[key])) {
          const current = sectionGroup.sections
          const previous = formHandler.data.sections

          sectionsDifference = differenceBy(current, previous, Properties.ID)

          setToTransferSections(sectionsDifference)

          setFormHandler(form => ({ ...form, data: sectionGroup }))
        }
      })
      .finally(() => {
        if (!isError || sectionsDifference.length === 0) {
          handleCloseModal()
        }
        setLoading(false)
      })
  }, [formHandler.data, sectionGroupHandler.upsert])

  const handleTransferSections = useCallback(() => {
    setLoading(true)

    sectionGroupHandler
      .transferSections(sectionsGroups.data, formHandler.data, toTransferSections)
      .then(formHandler.callback)
      .finally(handleCloseModal)
  }, [formHandler.data, sectionsGroups.data, toTransferSections, sectionGroupHandler.transferSections, formHandler.callback])

  const handleDelete = useCallback(() => {
    setLoading(true)
    sectionGroupHandler.delete(formHandler.data).finally(handleCloseModal)
  }, [formHandler.data, sectionGroupHandler.delete])

  const handleCloseModal = useCallback(() => {
    setToTransferSections([])
    setFormHandler(DEFAULT_FORM_HANDLER)
  }, [])

  const handleCancelTransfer = useCallback(group => {
    setFormHandler(form => ({ ...form, data: group }))
    setToTransferSections([])
  }, [])

  const sectionsToHighlight = useMemo(() => sectionsGroups.data.flatMap(sg => sg.sections), [sectionsGroups.data])

  const action = formHandler.data?.id === -1 ? Actions.CREATE.toLocaleLowerCase() : Actions.UPDATE.toLocaleLowerCase()

  return (
    <>
      <SectionGroupModalContext.Provider value={{ setFormHandler }}>
        {children}
      </SectionGroupModalContext.Provider>
      <Modal
        width={isNonEmptyArray(toTransferSections) ? 'fit-content' : 600}
        className='form-modal-without-footer'
        title={t(`section_group_form_modal.title.${action}`)}
        visible={isObject(formHandler.data)}
        onCancel={handleCloseModal}
        afterClose={() => setLoading(false)}
        destroyOnClose
        footer={null}
      >
        {isObject(formHandler.data) && isEmptyArray(toTransferSections) && (
          <SingleSectionGroupForm
            data={formHandler.data}
            sectionsToHighlight={sectionsToHighlight}
            disabled={loading}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onCancel={handleCloseModal}
          />
        )}
        {isObject(formHandler.data) && isNonEmptyArray(toTransferSections) && (
          <TransferSectionsPanel
            sources={sectionsGroups.data}
            destination={formHandler.data}
            toTransfert={toTransferSections}
            onCancel={handleCancelTransfer}
            onConfirm={handleTransferSections}
          />
        )}
      </Modal>
    </>
  )
}

export default SectionGroupModalProvider
